import {Avatar, Button, List} from 'antd'
import {host_searchHostsByOwner_api} from 'apis'
import {getId, getType} from 'apis/model/base'
import classNames from 'classnames'
import getAvatar from 'helpers/getAvatar'
import getTitle from 'helpers/getTitle'
import _ from 'lodash'
import Async from 'modules/asyncCache/components/Async'
import Translate from 'modules/local/Translate'
import useTranslate from 'modules/local/useTranslate'
import {NavigationContext} from 'modules/navigation/NavigationContext'
import React, {useCallback, useContext, useState} from 'react'
import {Redirect} from 'react-router-dom'
import AntdConfigWrapper from 'views/AppLocals/custom/AntdConfigWrapper'
import {useDebounceSearch} from 'views/AppLocals/custom/DebounceSearch'
import {bindQueryParams} from 'views/AppLocals/functions/routerHelper'
import {getResponseItems, NullAsDefault} from 'views/Shared'
import {useHostActions} from '../HostContext'
import {fromHostStatus} from '../Settings/HostContent'
import {renderIfElse} from "../../../Shared";
import {SearchGroupIcon} from "../../../../components/AppIcon";
import {useAppConfig} from "../../../../modules/local/AppConfigContext";
import LoadingBlock from "../../../Loading/LoadingBlock";

const renderRow =
    ({onClick = NullAsDefault}) =>
        (item, index) => {
            const {certified, owner = {}, owner_type, excluded = false} = item || {}
            const id = getId(owner)
            const prop = getType(owner)
            const host_status = fromHostStatus(-1)
            const {org_types = {}} = useAppConfig('org_types')
            const org_type = _.get(org_types, _.get(owner, 'org_type', '900'))

            return (
                <Translate key={index}>
                    {(t) => (
                        <div className="mb-3 flex items-center gap-2 custom-render-border rounded-lg relative">
                            <Avatar
                                src={getAvatar(owner)}
                                size={60}
                                className="flex flex-center background-200 rounded-l-lg rounded-r-0"
                                icon={
                                    <SearchGroupIcon size={30} type={getType(owner)} className="text-color-500"/>
                                }
                            />
                            <div className="flex flex-col flex-1">
                          <span className="font-bold text-base text-color-000 leading-snug">
                              {getTitle(owner)}
                          </span>
                                <span className="font-medium text-xs text-blue-500 italic leading-snug">
                              {renderIfElse(
                                  owner_type === 'organization',
                                  t(org_type),
                                  t(owner_type)
                              )}
                          </span>
                            </div>
                            {renderIfElse(
                                excluded,
                                <div
                                    className={classNames(
                                        host_status.className,
                                        'flex items-center gap-1 rounded-md px-2'
                                    )}>
                                    {host_status.icon}
                                    <span className="text-xs italic">{t(host_status.label)}</span>
                                </div>,
                                <Button
                                    color="primary" variant="filled"
                                    size="small"
                                    className="button-rounded-md font-medium text-xs text-primary italic"
                                    onClick={() => onClick(id, prop)}>
                                    {t('send request')}
                                </Button>
                            )}
                        </div>
                    )}
                </Translate>
            )
        }

export const HostPickerModal = ({
                                    item,
                                    owner_id,
                                    owner_type,
                                    Wrapper = 'div',
                                }) => {
    const t = useTranslate()

    const [excluded_ids, set_excluded_ids] = useState([])

    const apiInfo = host_searchHostsByOwner_api

    const [keyword, __, searchInput] = useDebounceSearch({
        timeout: 500,
        className: 'w-full rounded-md px-2 py-1',
    })

    const {onCreate = NullAsDefault} = useHostActions({
        container: item,
        onCreated: (result, data) => {
            const host_id = getId(_.get(data, 'response.data.host'))
            set_excluded_ids([...excluded_ids, host_id])
        },
    })

    const renderHost = useCallback(
        (item, index) =>
            renderRow({
                onClick: (host_id, host_type) =>
                    onCreate({
                        host_id,
                        host_type,
                    }),
            })(item, index),
        [onCreate]
    )

    return (
        <Wrapper className="flex flex-col">
            <div className="text-center font-bold uppercase text-color-000">
                {t('add host')}
            </div>
            <div className="mt-6 mb-3">
                {searchInput}
            </div>
            <Async
                key={keyword}
                query={bindQueryParams([{id: owner_id}, {prop: owner_type}])}
                values={{
                    keyword,
                }}
                apiInfo={apiInfo}>
                {({response, isLoading}) => {
                    if (Boolean(isLoading)) {
                        return <LoadingBlock/>
                    }
                    const items = getResponseItems(response) || []
                    return (
                        <List
                            loading={!!isLoading}
                            dataSource={items}
                            renderItem={(item, index) =>
                                renderHost({
                                    ...item,
                                    excluded: excluded_ids.includes(getId(item)),
                                })
                            }
                            pagination={{
                                pageSize: 8,
                                hideOnSinglePage: true,
                            }}
                        />
                    )
                }}
            </Async>
        </Wrapper>
    )
}

export const AddHostModalContent = ({Wrapper = 'div', ...props}) => {
    const t = useTranslate()

    const [excluded_ids, set_excluded_ids] = useState([])

    const {setCurrent} = useContext(NavigationContext)

    const [keyword, __, searchInput] = useDebounceSearch({
        timeout: 500,
        className: 'w-full rounded-md px-2 py-1',
    })

    const apiInfo = host_searchHostsByOwner_api

    const {
        id = props?.id,
        prop = props?.prop
    } = _.get(props, 'location.state', {});

    const {onCreate = NullAsDefault} = useHostActions({
        container: {
            id,
            _type: prop,
        },
        onCreated: (result, data) => {
            const host_id = getId(_.get(data, 'response.data.host'))
            setCurrent(result)
            set_excluded_ids([...excluded_ids, host_id])
        },
    })

    const renderHost = useCallback(
        (item, index) =>
            renderRow({
                onClick: (host_id, host_type) =>
                    onCreate({
                        host_id,
                        host_type,
                    }),
            })(item, index),
        [onCreate]
    )

    if (_.every([id, prop], _.isEmpty)) {
        return <Redirect to="/"/>
    }

    return (
        <Wrapper className="flex flex-col">
            <div className="text-center font-bold uppercase text-color-000">
                {t('add host')}
            </div>
            <div className="mt-6 mb-3">
                {searchInput}
            </div>
            <Async
                key={keyword}
                query={bindQueryParams([{id}, {prop}])}
                values={{
                    keyword,
                }}
                apiInfo={apiInfo}>
                {({response, isLoading}) => {
                    if (Boolean(isLoading)) {
                        return <LoadingBlock/>
                    }
                    const items = getResponseItems(response) || []
                    return (
                        <List
                            loading={!!isLoading}
                            dataSource={items}
                            renderItem={(item, index) =>
                                renderHost({
                                    ...item,
                                    excluded: excluded_ids.includes(getId(item)),
                                })
                            }
                            pagination={{
                                pageSize: 8,
                                hideOnSinglePage: true,
                            }}
                        />
                    )
                }}
            </Async>
        </Wrapper>
    )
}

const AddHostModal = (props) => (
    <AntdConfigWrapper>
        <AddHostModalContent {...props} />
    </AntdConfigWrapper>
)

export default AddHostModal
